import React, { useEffect, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { Package, X, Info, AlertTriangle } from 'lucide-react';

/**
 * PackageMetadataModal (WP-5 create + WP-7 edit)
 *
 * mode='create' (Save as Package): collects title/description/tags, shows
 * computed metadata + validation notices/warnings.
 * mode='edit' (management tag/metadata edit): pure metadata — prefills tags from
 * initialTags and hides the compile-only sections. Placeholder styling (WP-8).
 */
export default function PackageMetadataModal({
  open,
  onClose,
  onSubmit,
  initial = {},
  computed = {},
  validation = {},
  busy = false,
  mode = 'create',
  initialTags = []
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState([]);
  const [tagDraft, setTagDraft] = useState('');

  useEffect(() => {
    if (open) {
      setTitle(initial.title || '');
      setDescription(initial.description || '');
      setTags(mode === 'edit' ? initialTags : []);
      setTagDraft('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const commitTag = () => {
    const t = tagDraft.trim().replace(/,$/, '').trim();
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setTagDraft('');
  };

  const onTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitTag();
    } else if (e.key === 'Backspace' && !tagDraft && tags.length) {
      setTags(tags.slice(0, -1));
    }
  };

  const canSave = title.trim().length > 0 && !busy;
  const warnings = validation.warnings || [];
  const notices = validation.notices || [];
  const variableNames = computed.variableNames || [];

  return (
    <Dialog open={open} onClose={busy ? () => {} : onClose} className="pkg-modal-root" style={{ position: 'fixed', inset: 0, zIndex: 2000 }}>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)' }} aria-hidden="true" />
      <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <Dialog.Panel style={{ background: '#fff', borderRadius: 8, boxShadow: '0 10px 30px rgba(0,0,0,0.2)', width: 'min(560px, 96vw)', maxHeight: '90vh', overflow: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 18px', borderBottom: '1px solid #E8EAED' }}>
            <Package size={18} color="#3D85E6" />
            <Dialog.Title style={{ fontSize: 16, fontWeight: 600, flex: 1 }}>
              {mode === 'edit' ? 'Edit package' : 'Save as Package'}
            </Dialog.Title>
            <button onClick={onClose} disabled={busy} title="Close" style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>
              <X size={18} />
            </button>
          </div>

          <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
              <span style={{ fontWeight: 600 }}>Title <span style={{ color: '#d9534f' }}>*</span></span>
              <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Consent explainer" style={{ padding: '8px 10px', border: '1px solid #CBD2D9', borderRadius: 4, fontSize: 14 }} />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
              <span style={{ fontWeight: 600 }}>Description</span>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="What this package is for" style={{ padding: '8px 10px', border: '1px solid #CBD2D9', borderRadius: 4, fontSize: 14, resize: 'vertical' }} />
            </label>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
              <span style={{ fontWeight: 600 }}>Purpose tags</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: 6, border: '1px solid #CBD2D9', borderRadius: 4, minHeight: 38, alignItems: 'center' }}>
                {tags.map((t) => (
                  <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#EBF4FF', color: '#2B6BBF', borderRadius: 12, padding: '2px 8px', fontSize: 12 }}>
                    {t}
                    <button onClick={() => setTags(tags.filter((x) => x !== t))} style={{ border: 'none', background: 'transparent', cursor: 'pointer', lineHeight: 1, color: '#2B6BBF' }}>×</button>
                  </span>
                ))}
                <input value={tagDraft} onChange={(e) => setTagDraft(e.target.value)} onKeyDown={onTagKeyDown} onBlur={commitTag} placeholder={tags.length ? '' : 'explanation, consent, demographics…'} style={{ flex: 1, minWidth: 120, border: 'none', outline: 'none', fontSize: 13, padding: '2px 0' }} />
              </div>
              <span style={{ color: '#889', fontSize: 11 }}>Press Enter or comma to add a tag.</span>
            </div>

            {mode !== 'edit' && (
              <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#556', background: 'var(--color-bg-base)', borderRadius: 4, padding: '8px 12px' }}>
                <span><strong>{computed.questionCount ?? 0}</strong> question(s)</span>
                <span style={{ flex: 1 }}><strong>{variableNames.length}</strong> variable(s){variableNames.length ? `: ${variableNames.join(', ')}` : ''}</span>
              </div>
            )}

            {mode !== 'edit' && notices.length > 0 && (
              <div style={{ fontSize: 12, color: '#31708f', background: '#EBF4FF', borderRadius: 4, padding: '8px 12px' }}>
                {notices.map((n, i) => (<div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}><Info size={13} style={{ marginTop: 1, flexShrink: 0 }} /> {n}</div>))}
              </div>
            )}
            {mode !== 'edit' && warnings.length > 0 && (
              <div style={{ fontSize: 12, color: '#8a6d3b', background: '#FCF8E3', borderRadius: 4, padding: '8px 12px' }}>
                {warnings.map((w, i) => (<div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}><AlertTriangle size={13} style={{ marginTop: 1, flexShrink: 0 }} /> {w}</div>))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px', borderTop: '1px solid #E8EAED' }}>
            <button onClick={onClose} disabled={busy} style={{ padding: '8px 14px', border: '1px solid #CBD2D9', background: '#fff', borderRadius: 4, cursor: 'pointer' }}>Cancel</button>
            <button onClick={() => onSubmit({ title: title.trim(), description: description.trim(), tags })} disabled={!canSave} style={{ padding: '8px 14px', border: '1px solid #3D85E6', background: canSave ? '#5B9FFF' : '#A9C9F5', color: '#fff', borderRadius: 4, cursor: canSave ? 'pointer' : 'not-allowed' }}>
              {busy ? 'Saving…' : (mode === 'edit' ? 'Save changes' : 'Save Package')}
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
